import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import {ObjectId} from 'bson'

import { Button, Animation } from 'components'
import { itemContext } from 'context'
import { Tag } from 'schema'
import { randomTagColor } from 'utils'
import { FaCircle, FaExclamationCircle, FaExclamation, FaCheck } from 'react-icons/fa'

import * as actionCreators from 'store/actions';

const defaultStyle = {
    backgroundColor: 'var(--primary-color)',
    border: '1px solid var(--primary-accent-color)',
    boxShadow: 'var(--box-shadow)',
    width: '15rem'
}

const Container = styled.div`
    border-radius: 5px;
    padding: 0.1rem;
    background-color: ${props => props.styles.backgroundColor};
    border: ${props => props.styles.border};
    box-shadow: ${props => props.styles.boxShadow};
    width: ${props => props.styles.width};
    display: flex;
    flex-direction: column;
    max-height: 10rem;
    overflow-y: auto;
`

const Title = styled.div`
    font-weight: bolder;
    font-size: 0.7rem;
    padding: 0.5rem 0.5rem 0 0.6rem;
    text-transform: uppercase;
    color: var(--secondary-accent-color);
`

const Subtitle = styled.div`
    font-weight: bolder;
    font-size: 0.7rem;
    padding: 0.25rem 0 0 0.5rem;
    text-transform: uppercase;
    color: var(--secondary-accent-color);
`

const Placeholder = styled.div`
    font-size: 0.8rem;
    padding: 0.5rem 0.5rem 0.5rem 0.6rem;
    color: var(--secondary-accent-color);
`

const Wrapper = styled.div`
    border: ${props => `2px solid ${props.selected ? 'var(--focus-color)' : 'transparent'}`};
    border-radius: 5px;
`

const Row = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1rem 1fr 1rem;
    grid-column-gap: 0.5rem;
    align-items: center;
    padding: ${props => props.styles.padding || '0.5rem 0.5rem'};
    background-color: var(--secondary-color);
    border-radius: 5px;
    cursor: pointer;
    :hover{
        filter: brightness(var(--brightness));
    }
`

const Text = styled.div`
    /* font-weight: bold; */
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Icon = styled.div`
    color: ${props => props.color || 'var(--text-color)'};
    display: flex;
    font-size: 0.8rem;
`

const TagSelector = props => {
    const {
        styles,
        onSelect,
        onClose,
        context,
        title,
        selected,
        pageIDs,
        pageID
    } = props;


    // Redux
    const dispatch = useDispatch()
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const tags = data['tags']
    const activePageID = useSelector((state) => state.auth.activePageID)
    const user = useSelector((state) => state.auth.user)

    // Refs
    const containerRef = React.useRef()
    const optionsRef = React.useRef([])
    
    // State
    const updatedStyle = Object.assign({}, defaultStyle, styles)
    const [n, setIndex] = React.useState(-1)

    React.useEffect(() => { // To ensure event listener updates
        const onKeyPress = window.addEventListener('keydown', keyHandler, true)
        return () => {window.removeEventListener('keydown', keyHandler, true)}
    }, [n, tags])

    React.useEffect(() => { // To close when got no text
        context === '' && onClose && onClose()
    }, [context])

    // Create options
    let options = []
    let placeholder
    let newTag

    // Populate with existing tags
    let pageIDsRef = []
    const selectedPageIDs = pageIDs ? pageIDs : Object.keys(data['pages'])
    let filteredTags = Object.values(tags).filter(tag => selectedPageIDs.includes(tag['pageID']))
    filteredTags.map((tag,index) => {
        // Page header
        !pageIDsRef.includes(tag['pageID']) && !pageID && options.push({
            type: 'subtitle',
            title: data['pages'][tag['pageID']]['page_name']
        })

        // Tag option
        options.push({
            type: 'option',
            title: tag['content'],
            value: tag['tagID'],
            icon: <FaCircle style={{color: tag['color']}}/>,
            styles: {
                backgroundColor: updatedStyle['backgroundColor'], 
                boxShadowColor: 'transparent',
                fontWeight: 'bold',
                margin: '0.1rem 0',
                justifyContent: 'flex-start',
            },
            onSelect: () => onSelect && onSelect(tag['tagID'])
        })

        pageIDsRef.push(tag['pageID'])
    })

    // Placeholder if no tags
    placeholder = <Placeholder>No tags created</Placeholder>

    const keyHandler = (event) => {
        const max = options.length - 2
        let i
        switch(event.keyCode){
            case 40: // Down
                i = n <= max ? n + 1 
                    : n > max ? 0
                    : n
                setIndex(i)
                scrollHandler('down',i)
                break
            case 38: // Up
                i = n > 0 ? n - 1 
                    : n <= 0 ? max + 1
                    : n
                setIndex(i)
                scrollHandler('up',i)
                break
            case 13: // Enter
                onSelect && setTimeout(() => options[n] && options[n]['onSelect'](), 100)
                break
            case 27: // Esc
                break
            case 8: // Backspace
                console.log('[BACKSPACE]')
                break
        }
    }

    const scrollHandler = (direction, index) => {
        if(!containerRef.current) { return }

        const containerRect = containerRef.current.getBoundingClientRect()
        let blocks = options
        const bottoms = blocks.map((block, i) => optionsRef.current[i] && optionsRef.current[i].getBoundingClientRect()['bottom'] - containerRect['y'])
        const tops = blocks.map((block, i) => optionsRef.current[i] && optionsRef.current[i].getBoundingClientRect()['top'] - containerRect['y'])

        let scrollTo = -1
        if(index === blocks.length){
            scrollTo = containerRect['height'] + 1000
        } else if (index === 0) {
            scrollTo = 0
        } else {
            scrollTo = bottoms[index] > containerRect['height'] ? tops[index] + containerRef.current.scrollTop
                    // : tops[index] < 0 ? Math.abs(bottoms[index]) + Math.abs(tops[index])
                    : tops[index] < 0 ? containerRef.current.scrollTop + tops[index]
                    : -1
        } 
        scrollTo > -1 && containerRef.current.scrollTo({left: 0, top: scrollTo, behavior: 'smooth'})
    }
    return (
        <Container styles={updatedStyle} ref={containerRef}>     
            {/* {context}   */}
            {title && <Title>{title}</Title>}
            {options.length === 0 && placeholder}
            {options.map((option,index) => {
                if(option['type'] === 'option'){
                    return <Wrapper selected={n === index} ref={el => optionsRef.current[index] = el}>
                                {/* <Button text={option['title']} 
                                    icon={option['icon']}
                                    onSelect={option['onSelect']} 
                                    styles={option['styles']}/> */}
                                
                                <Row onClick={option['onSelect']} styles={{...option['styles'], padding: pageID ? '0.5rem 0.25rem' : option['styles'] && option['styles']['padding']}}>
                                    <Icon>{option['icon']}</Icon>
                                    <Text>{option['title']}</Text>
                                    {selected && ((option['value'] === selected | (Array.isArray(selected) && selected.includes(option['value'])))) ? <FaCheck style={{ color: 'var(--focus-color)' }}/> : null}
                                </Row>
                            </Wrapper>
                } else if (option['type'] === 'subtitle') {
                    return <Subtitle>{option['title']}</Subtitle>
                }
            })}     
        </Container>
    )
}

export default TagSelector